import { DataAnalyzer } from '../../utils/dataAnalyzer';
import { DataQAEngine } from '../../utils/dataQAEngine';
import { EvidenceInsightsEngine } from '../analytics/evidenceInsightsEngine';
import { EvidenceInsight } from '../analytics/types';
import { CleaningPreview, CleaningResult, DataQualityEngine } from '../quality/dataQualityEngine';
import {
  AlertRule,
  CleaningApplyResponse,
  CleaningPreviewResponse,
  DatasetProfileResponse,
  InsightResponse,
  QAAskResponse,
  UploadDatasetResponse
} from './apiContracts';

interface DatasetVersion {
  id: string;
  createdAt: string;
  rows: any[];
  note: string;
}

interface DatasetRecord {
  datasetId: string;
  fileName: string;
  headers: string[];
  versions: DatasetVersion[];
}

interface ConnectionRecord {
  id: string;
  name: string;
  type: 'google_sheets' | 'postgres';
  status: 'active' | 'inactive';
  createdAt: string;
}

const DATASET_KEY = 'vizdata_api_datasets';
const ALERT_KEY = 'vizdata_api_alerts';
const CONNECTION_KEY = 'vizdata_api_connections';

const id = (prefix: string): string => `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
const delay = async (ms = 120): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const readJSON = <T>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const writeJSON = <T>(key: string, value: T): void => {
  localStorage.setItem(key, JSON.stringify(value));
};

const getRecords = (): DatasetRecord[] => readJSON<DatasetRecord[]>(DATASET_KEY, []);
const saveRecords = (records: DatasetRecord[]): void => writeJSON(DATASET_KEY, records);

const latestVersion = (record: DatasetRecord): DatasetVersion => record.versions[record.versions.length - 1];

export interface DatasetSnapshot {
  datasetId: string;
  fileName: string;
  headers: string[];
  rows: any[];
  versionId: string;
  versionCount: number;
}

export class ApiService {
  async uploadDataset(fileName: string, headers: string[], rows: any[]): Promise<UploadDatasetResponse> {
    await delay();
    const records = getRecords();
    const normalizedRows = rows.slice(0, 8000);

    const datasetId = id('ds');
    const record: DatasetRecord = {
      datasetId,
      fileName,
      headers,
      versions: [{ id: id('ver'), createdAt: new Date().toISOString(), rows: normalizedRows, note: 'Initial upload' }]
    };

    records.push(record);
    this.persistWithQuotaGuard(records);

    return { datasetId, status: 'processed' };
  }

  async getDataset(datasetId: string): Promise<DatasetSnapshot | null> {
    await delay(80);
    const record = getRecords().find((item) => item.datasetId === datasetId);
    if (!record) return null;

    const version = latestVersion(record);
    return {
      datasetId: record.datasetId,
      fileName: record.fileName,
      headers: record.headers,
      rows: version.rows,
      versionId: version.id,
      versionCount: record.versions.length
    };
  }

  async getDatasetProfile(datasetId: string): Promise<DatasetProfileResponse> {
    await delay(100);
    const snapshot = await this.getDataset(datasetId);
    if (!snapshot) {
      throw new Error('Dataset not found');
    }

    const analyzer = new DataAnalyzer(snapshot.rows, snapshot.headers);
    const engine = new EvidenceInsightsEngine(snapshot.rows, snapshot.headers, analyzer.getColumnInfo());
    const profile = engine.getProfile();

    return { datasetId, profile };
  }

  async getInsights(datasetId: string): Promise<InsightResponse> {
    await delay(120);
    const snapshot = await this.getDataset(datasetId);
    if (!snapshot) throw new Error('Dataset not found');

    const analyzer = new DataAnalyzer(snapshot.rows, snapshot.headers);
    const engine = new EvidenceInsightsEngine(snapshot.rows, snapshot.headers, analyzer.getColumnInfo());
    const recommendations = analyzer.getChartRecommendations();

    const insights: EvidenceInsight[] = recommendations.flatMap((recommendation) => engine.generateInsights(recommendation));
    return { datasetId, insights };
  }

  async getCleaningPreview(datasetId: string): Promise<CleaningPreviewResponse> {
    await delay(100);
    const snapshot = await this.getDataset(datasetId);
    if (!snapshot) throw new Error('Dataset not found');

    const engine = new DataQualityEngine(snapshot.rows, snapshot.headers);
    const preview = engine.buildPreview();

    return { datasetId, preview };
  }

  async applyCleaning(datasetId: string, operations?: CleaningPreview['operations']): Promise<CleaningApplyResponse & { newDatasetVersionId: string }> {
    await delay(140);
    const records = getRecords();
    const index = records.findIndex((record) => record.datasetId === datasetId);
    if (index < 0) throw new Error('Dataset not found');

    const record = records[index];
    const snapshot = latestVersion(record);
    const engine = new DataQualityEngine(snapshot.rows, record.headers);

    const resolvedOperations = operations ?? engine.buildPreview().operations;
    const result: CleaningResult = engine.applyOperations(resolvedOperations);

    const nextVersion: DatasetVersion = {
      id: id('ver'),
      createdAt: new Date().toISOString(),
      rows: result.cleanedRows,
      note: 'Applied safe cleaning operations'
    };

    record.versions.push(nextVersion);
    records[index] = record;
    this.persistWithQuotaGuard(records);

    return {
      datasetId,
      result,
      newDatasetVersionId: nextVersion.id
    };
  }

  async askQuestion(datasetId: string, question: string): Promise<QAAskResponse> {
    await delay(110);
    const snapshot = await this.getDataset(datasetId);
    if (!snapshot) throw new Error('Dataset not found');

    const analyzer = new DataAnalyzer(snapshot.rows, snapshot.headers);
    const engine = new DataQAEngine(snapshot.rows, snapshot.headers, analyzer.getColumnInfo());
    const result = engine.answerQuestion(question);

    return {
      question,
      answer: result.answer,
      confidence: result.confidence,
      citations: result.citations ?? [{ label: 'Dataset', detail: `${snapshot.rows.length} rows analyzed.` }]
    };
  }

  async listAlerts(): Promise<AlertRule[]> {
    await delay(80);
    return readJSON<AlertRule[]>(ALERT_KEY, []);
  }

  async createAlert(rule: Omit<AlertRule, 'id'>): Promise<AlertRule> {
    await delay(100);
    const current = readJSON<AlertRule[]>(ALERT_KEY, []);
    const next: AlertRule = { ...rule, id: id('alert') };
    current.push(next);
    writeJSON(ALERT_KEY, current);
    return next;
  }

  async listConnections(): Promise<ConnectionRecord[]> {
    await delay(80);
    return readJSON<ConnectionRecord[]>(CONNECTION_KEY, []);
  }

  async createConnection(input: Omit<ConnectionRecord, 'id' | 'createdAt' | 'status'>): Promise<ConnectionRecord> {
    await delay(110);
    const current = readJSON<ConnectionRecord[]>(CONNECTION_KEY, []);
    const next: ConnectionRecord = {
      ...input,
      id: id('conn'),
      createdAt: new Date().toISOString(),
      status: 'active'
    };
    current.push(next);
    writeJSON(CONNECTION_KEY, current);
    return next;
  }

  private persistWithQuotaGuard(records: DatasetRecord[]): void {
    const capped = records.slice(-8);
    try {
      saveRecords(capped);
      return;
    } catch {
      // Remove older records progressively when storage pressure is high.
    }

    for (let start = 1; start < capped.length; start += 1) {
      try {
        saveRecords(capped.slice(start));
        return;
      } catch {
        // keep trimming
      }
    }

    throw new Error('Browser storage is full. Clear old saved datasets and retry.');
  }
}

export const apiService = new ApiService();
